import { PAGE_TYPES } from '@/dashboard/enums/page-types';
import locales from '@/dashboard/services/countries/locales';
import {
  THEME_PROPERTY_REGISTRY,
  THEME_OPTIONS,
} from '@repo/checkout/theming/themePropertyRegistry';
import { isMultiPrice } from '@repo/checkout/lib/multiPrice';
import { getCoverProcessingFeesSetting } from '@repo/lib';

export const THEME_FIELD_TYPES = {
  COLOR_DUAL: 'color-select-dual',
  ELEMENT: 'element',
  TEXT: 'text',
  SELECT: 'select',
  TOGGLE: 'toggle',
  FONT_GROUP: 'font-group',
  FONT_COMBOBOX: 'font-combobox',
  FONT_WEIGHT_SELECT: 'font-weight-select',
  IMAGE: 'image',
};

export const fontSizeOptions = THEME_OPTIONS.fontSize;
export const fontStyleOptions = THEME_OPTIONS.fontStyle;

export const THEME_FIELD_DEFAULTS = {
  [THEME_FIELD_TYPES.COLOR_DUAL]: { light: '', dark: '' },
  [THEME_FIELD_TYPES.TEXT]: {},
  [THEME_FIELD_TYPES.SELECT]: { height: 'small' },
  [THEME_FIELD_TYPES.TOGGLE]: {},
  [THEME_FIELD_TYPES.FONT_COMBOBOX]: { height: 'small' },
  [THEME_FIELD_TYPES.FONT_WEIGHT_SELECT]: { height: 'small' },
};

// Map registry property types to dashboard field types
const TYPE_MAP = {
  color: THEME_FIELD_TYPES.COLOR_DUAL,
  font: THEME_FIELD_TYPES.FONT_COMBOBOX,
  fontWeight: THEME_FIELD_TYPES.FONT_WEIGHT_SELECT,
  fontStyle: THEME_FIELD_TYPES.SELECT,
  fontSize: THEME_FIELD_TYPES.SELECT,
  size: THEME_FIELD_TYPES.SELECT,
  shadow: THEME_FIELD_TYPES.SELECT,
  alignment: THEME_FIELD_TYPES.SELECT,
  select: THEME_FIELD_TYPES.SELECT,
  toggle: THEME_FIELD_TYPES.TOGGLE,
  image: THEME_FIELD_TYPES.IMAGE,
  text: THEME_FIELD_TYPES.TEXT,
};

function resolveOptions(prop) {
  if (!prop.options) {
    if (prop.type === 'fontStyle') return THEME_OPTIONS.fontStyle;
    if (prop.type === 'fontSize') return THEME_OPTIONS.fontSize;
    return undefined;
  }
  if (typeof prop.options === 'string') {
    return THEME_OPTIONS[prop.options];
  }
  return prop.options;
}

function generateThemeFieldRegistry() {
  const registry = {};

  for (const prop of THEME_PROPERTY_REGISTRY) {
    // Skip font sub-fields generated by font-group builder at runtime
    if (
      prop.path.startsWith('variables.elements.') &&
      (prop.type === 'font' ||
        prop.type === 'fontStyle' ||
        prop.type === 'fontWeight' ||
        prop.type === 'fontSize')
    ) {
      continue;
    }

    const fieldType = TYPE_MAP[prop.type];
    if (!fieldType) continue;

    const entry = {
      type: fieldType,
      label: prop.label,
    };

    const options = resolveOptions(prop);
    if (options) entry.options = options;

    if (prop.resetable) entry.resetable = true;
    if (prop.defaultValue !== undefined) entry.defaultValue = prop.defaultValue;

    // Font weight specific
    if (prop.type === 'fontWeight' && prop.fontFamilyField) {
      entry.fontFamilyFieldName = prop.fontFamilyField;
    }
    if (prop.uiMeta?.showAutoOption !== undefined) {
      entry.showAutoOption = prop.uiMeta.showAutoOption;
    }

    // Font specific
    if (prop.uiMeta?.defaultLabel) entry.defaultLabel = prop.uiMeta.defaultLabel;
    if (prop.parentFontField) entry.parentFontField = prop.parentFontField;

    // A value that overrules another field carries the write with it — the form
    // applies it on change, so it has to survive into the field config.
    if (prop.setAlso) entry.setAlso = prop.setAlso;

    // Color specific
    if (prop.uiMeta?.picker) entry.picker = prop.uiMeta.picker;

    // Image specific
    if (prop.uiMeta?.previewSize) entry.previewSize = prop.uiMeta.previewSize;
    if (prop.type === 'image') entry.variant = 'design-form';

    registry[prop.path] = entry;
  }

  // Locale options are a dashboard concern (not in the shared property registry)
  if (registry['variables.locale']) {
    registry['variables.locale'].options = locales.map(l => ({
      value: l.code,
      label: l.country,
    }));
  }

  return registry;
}

export const THEME_FIELD_REGISTRY = generateThemeFieldRegistry();

// Unset is beside the offer, the appearance's default, so it has to read as a
// chosen Beside the offer does: only a row or a bar is a line of its own.
const isCallToActionBesideOffer = values =>
  !['row', 'bar'].includes(
    values?.variables?.elements?.checkout?.form?.orderBumps?.acceptAppearance,
  );

const isMiddleEdge = option => option.value.startsWith('center-');

const isCardCentered = values =>
  values?.variables?.elements?.checkout?.form?.orderBumps?.horizontalAlign === 'center';

// Order bumps on a checkout, add-ons on an event: one placement list, one
// set of theme controls.
const carriesOrderBumps = page =>
  page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT;

// The editor's page object carries a bump only as a placement entry: the
// products behind them are grafted on by the storefront (and the preview), so
// without them any placed bump has to count as a pay-what-you-want one.
const hasPwywOrderBump = page => {
  if (!carriesOrderBumps(page)) return false;
  if (!(page.orderBumps || []).some(bump => bump.enabled !== false)) return false;
  if (!page.orderBumpProducts) return true;
  return page.orderBumpProducts.some(product =>
    (product.prices || []).some(price => price.enabled !== false && price.payWhatYouWant),
  );
};

export const THEME_FIELD_STRUCTURE = {
  'variables.tokens': {
    type: 'fieldgroup',
    id: 'global',
    divider: true,
    collapsible: true,
    shouldBeOpen: () => true,
    fields: [
      {
        type: 'heading',
        label: 'Global',
        description: 'Colors, typography, and structure used across the page.',
        level: 3,
        id: 'global',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.tokens',
          'variables.locale',
          'variables.layout.shadowColor',
          'variables.layout.shadowWidth',
        ],
      },
      { type: 'subheader', label: 'Mode' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.tokens.colors'],
      },
      { type: 'subheader', label: 'Colors' },
      { type: 'subgroup', label: 'Brand' },
      {
        type: 'columns',
        columns: 2,
        fields: [
          { path: 'variables.tokens.colorPrimary', label: 'Main' },
          { path: 'variables.tokens.colorSecondary', label: 'Accent' },
        ],
      },
      { type: 'subgroup', label: 'Text' },
      {
        type: 'columns',
        columns: 2,
        fields: [
          { path: 'variables.tokens.textPrimary', label: 'Body' },
          { path: 'variables.tokens.textSecondary', label: 'Secondary' },
          { path: 'variables.tokens.textTertiary', label: 'Supporting' },
        ],
      },
      { type: 'subgroup', label: 'Columns' },
      {
        type: 'columns',
        columns: 2,
        fields: ['variables.tokens.colorSurface', 'variables.tokens.colorSurfaceAccent'],
      },
      { type: 'subgroup', label: 'Page' },
      {
        type: 'columns',
        columns: 2,
        fields: [
          'variables.tokens.colorBackground',
          { path: 'variables.layout.shadowColor', label: 'Shadow' },
        ],
      },
      { type: 'subheader', label: 'Typography' },
      {
        type: 'columns',
        columns: 1,
        // Match the 12px rhythm the Title/Text subgroups get from EyebrowGroup's
        // [&+&]:mt-1, since a standalone field doesn't trigger that sibling rule.
        wrapperClassName: 'mb-1',
        fields: [{ path: 'variables.tokens.fontMain.family', label: 'Base font' }],
      },
      { type: 'subgroup', label: 'Title' },
      {
        type: 'columns',
        columns: 3,
        fields: [
          { path: 'variables.tokens.fontHeading.family', label: 'Font' },
          { path: 'variables.tokens.fontHeading.weight', label: 'Weight' },
          { path: 'variables.tokens.fontHeading.size', label: 'Size' },
        ],
      },
      { type: 'subgroup', label: 'Text' },
      {
        type: 'columns',
        columns: 3,
        fields: [
          { path: 'variables.tokens.fontBody.family', label: 'Font' },
          { path: 'variables.tokens.fontBody.weight', label: 'Weight' },
          { path: 'variables.tokens.fontBody.size', label: 'Size' },
        ],
      },
      { type: 'subheader', label: 'Structure' },
      {
        type: 'columns',
        columns: 2,
        fields: ['variables.tokens.radius', 'variables.layout.shadowWidth'],
      },
      { type: 'subheader', label: 'Locale' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.locale'],
      },
    ],
  },
  'variables.elements.checkout': {
    type: 'fieldgroup',
    id: 'columns',
    divider: true,
    collapsible: true,
    shouldBeOpen: () => true,
    fields: [
      {
        type: 'heading',
        label: 'Columns',
        description: 'Backgrounds, borders, and padding for each checkout layout.',
        level: 3,
        id: 'columns',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.divider',
          'variables.elements.checkout.styles.borderColor',
          'variables.elements.checkout.styles.borderWidth',
          'variables.elements.checkout.styles.borderRadius',
          'variables.elements.checkout.styles.padding',
          'variables.layout.centerLayout',
          'variables.layout.doubleLayout',
          'variables.layout.splitLayout',
        ],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'columns-divider',
        label: 'Divider',
        fields: ['variables.elements.checkout.divider.styles.borderColor'],
        toggleKey: 'variables.elements.checkout.divider.enabled',
        resetKeys: ['variables.elements.checkout.divider'],
      },
      // Center layout subsection
      {
        type: 'fieldgroup',
        id: 'center-layout-settings',
        divider: false,
        collapsible: true,
        className: 'mt-3 first:mt-0',
        isOnPage: ({ page }) => (page?.hostedLayoutType || 'center') === 'center',
        shouldBeOpen: ({ page }) => (page?.hostedLayoutType || 'center') === 'center',
        fields: [
          {
            type: 'heading',
            label: 'Center layout',
            level: 4,
            id: 'center-layout',
            resetable: true,
            toggleable: false,
            resetKeys: [
              'variables.elements.checkout.styles.padding',
              'variables.elements.checkout.styles.borderColor',
              'variables.elements.checkout.styles.borderWidth',
              'variables.elements.checkout.styles.borderRadius',
              'variables.layout.centerLayout',
            ],
          },
          { type: 'subheader', label: 'Layout' },
          {
            type: 'columns',
            columns: 1,
            fields: ['variables.elements.checkout.styles.padding'],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'center-layout-page',
            label: 'Page',
            hideSwatch: true,
            showEye: false,
            fields: [
              'variables.layout.centerLayout.backgroundColor',
              'variables.layout.centerLayout.backgroundImage',
              'variables.layout.centerLayout.backgroundPosition',
            ],
            fieldLabels: {
              'variables.layout.centerLayout.backgroundColor': 'Background color',
              'variables.layout.centerLayout.backgroundImage': 'Image',
              'variables.layout.centerLayout.backgroundPosition': 'Background position',
            },
          },
          {
            type: 'element',
            id: 'center-layout-column',
            label: 'Column',
            hideSwatch: true,
            showEye: false,
            fields: [
              'variables.layout.centerLayout.contentBackgroundColor',
              'variables.layout.centerLayout.formBackgroundColor',
              'variables.elements.checkout.styles.borderColor',
              'variables.elements.checkout.styles.borderWidth',
              'variables.elements.checkout.styles.borderRadius',
            ],
            fieldLabels: {
              'variables.layout.centerLayout.contentBackgroundColor': 'Content background color',
              'variables.layout.centerLayout.formBackgroundColor': 'Form background color',
              'variables.elements.checkout.styles.borderColor': 'Border color',
              'variables.elements.checkout.styles.borderWidth': 'Border width',
              'variables.elements.checkout.styles.borderRadius': 'Border radius',
            },
          },
        ],
      },
      // Double column layout subsection
      {
        type: 'fieldgroup',
        id: 'double-layout-settings',
        divider: false,
        collapsible: true,
        isOnPage: ({ page }) => page?.hostedLayoutType === 'double',
        shouldBeOpen: ({ page }) => page?.hostedLayoutType === 'double',
        fields: [
          {
            type: 'heading',
            label: 'Double column layout',
            level: 4,
            id: 'double-layout',
            resetable: true,
            toggleable: false,
            resetKeys: ['variables.layout.doubleLayout'],
          },
          { type: 'subheader', label: 'Layout' },
          {
            type: 'columns',
            columns: 2,
            fields: [
              'variables.layout.doubleLayout.formPosition',
              'variables.layout.doubleLayout.backgroundWidth',
            ],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'double-layout-content-column',
            label: 'Content column',
            fields: ['variables.layout.doubleLayout.contentBackgroundColor'],
          },
          {
            type: 'element',
            id: 'double-layout-form-column',
            label: 'Form column',
            fields: ['variables.layout.doubleLayout.formBackgroundColor'],
          },
        ],
      },
      // Split layout subsection
      {
        type: 'fieldgroup',
        id: 'split-layout-settings',
        divider: false,
        collapsible: true,
        isOnPage: ({ page }) => page?.hostedLayoutType === 'split',
        shouldBeOpen: ({ page }) => page?.hostedLayoutType === 'split',
        fields: [
          {
            type: 'heading',
            label: 'Split layout',
            level: 4,
            id: 'split-layout',
            resetable: true,
            toggleable: false,
            resetKeys: ['variables.layout.splitLayout'],
          },
          { type: 'subheader', label: 'Layout' },
          {
            type: 'columns',
            columns: 1,
            fields: ['variables.layout.splitLayout.mainColumn'],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'split-layout-page',
            label: 'Page',
            hideSwatch: true,
            showEye: false,
            fields: [
              'variables.layout.splitLayout.backgroundColor',
              'variables.layout.splitLayout.backgroundImage',
              'variables.layout.splitLayout.backgroundPosition',
            ],
            fieldLabels: {
              'variables.layout.splitLayout.backgroundColor': 'Background color',
              'variables.layout.splitLayout.backgroundImage': 'Image',
              'variables.layout.splitLayout.backgroundPosition': 'Background position',
            },
          },
          {
            type: 'element',
            id: 'split-layout-column',
            label: 'Column',
            hideSwatch: true,
            showEye: false,
            fields: [
              'variables.layout.splitLayout.contentBackgroundColor',
              'variables.layout.splitLayout.formBackgroundColor',
            ],
            fieldLabels: {
              'variables.layout.splitLayout.contentBackgroundColor': 'Content background color',
              'variables.layout.splitLayout.formBackgroundColor': 'Form background color',
            },
          },
        ],
      },
    ],
  },
  'variables.elements.checkout.cards': {
    type: 'fieldgroup',
    id: 'cards',
    divider: true,
    collapsible: true,
    isOnPage: ({ page, product }) =>
      (page?.type === PAGE_TYPES.CHECKOUT &&
        (isMultiPrice(product?.prices) ||
          product?.variants?.length > 0 ||
          page?.paymentOptions?.some(
            option => option.type === 'manual' || option.type === 'partial' || option.enabled,
          ))) ||
      page?.type === PAGE_TYPES.EVENT,
    shouldBeOpen: ({ page, product }) => {
      return (
        (page?.type === PAGE_TYPES.CHECKOUT &&
          (isMultiPrice(product?.prices) ||
            product?.variants?.length > 0 ||
            page?.paymentOptions?.some(
              option => option.type === 'manual' || option.type === 'partial' || option.enabled,
            ))) ||
        page?.type === PAGE_TYPES.EVENT
      );
    },
    fields: [
      {
        type: 'heading',
        label: 'Cards',
        description: 'Shared styles for price options, variants, tickets, and payment methods.',
        level: 3,
        id: 'cards',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.cards'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'cards-card',
        label: 'Card',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.cards.styles.backgroundColor',
          'variables.elements.checkout.cards.selectedStyles.backgroundColor',
          'variables.elements.checkout.cards.styles.borderColor',
          'variables.elements.checkout.cards.selectedStyles.borderColor',
          'variables.elements.checkout.cards.styles.borderWidth',
          'variables.elements.checkout.cards.styles.borderRadius',
        ],
        fieldLabels: {
          'variables.elements.checkout.cards.styles.borderColor': 'Border color',
          'variables.elements.checkout.cards.styles.borderWidth': 'Border width',
          'variables.elements.checkout.cards.styles.borderRadius': 'Border radius',
        },
      },
      {
        type: 'element',
        id: 'cards-badge',
        label: 'Highlight',
        hideSwatch: true,
        fields: [
          'variables.elements.checkout.cards.badge.styles.color',
          'variables.elements.checkout.cards.badge.styles.backgroundColor',
          'variables.elements.checkout.cards.badge.styles.borderColor',
          'variables.elements.checkout.cards.badge.styles.borderWidth',
          'variables.elements.checkout.cards.badge.styles.borderRadius',
          'variables.elements.checkout.cards.badge.styles.padding',
        ],
        fontGroupBase: 'variables.elements.checkout.cards.badge.styles',
        fieldLabels: {
          'variables.elements.checkout.cards.badge.styles.color': 'Text color',
          'variables.elements.checkout.cards.badge.styles.backgroundColor': 'Background',
          'variables.elements.checkout.cards.badge.styles.borderColor': 'Border color',
          'variables.elements.checkout.cards.badge.styles.borderWidth': 'Border width',
          'variables.elements.checkout.cards.badge.styles.borderRadius': 'Border radius',
          'variables.elements.checkout.cards.badge.styles.padding': 'Padding',
        },
      },
      {
        type: 'element',
        id: 'cards-group-name',
        label: 'Group name',
        fields: [
          'variables.elements.checkout.cards.groupName.styles.color',
          'variables.elements.checkout.cards.groupName.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.cards.groupName.styles',
      },
      {
        type: 'element',
        id: 'cards-name',
        label: 'Name/title',
        fields: ['variables.elements.checkout.cards.name.styles.color'],
        fontGroupBase: 'variables.elements.checkout.cards.name.styles',
      },
      {
        type: 'element',
        id: 'cards-price',
        label: 'Price',
        fields: ['variables.elements.checkout.cards.price.styles.color'],
        fontGroupBase: 'variables.elements.checkout.cards.price.styles',
      },
      {
        type: 'element',
        id: 'cards-discounted-from-price',
        label: 'Discounted price',
        fields: ['variables.elements.checkout.cards.discountedFromPrice.styles.color'],
        fontGroupBase: 'variables.elements.checkout.cards.discountedFromPrice.styles',
      },
      {
        type: 'element',
        id: 'cards-description',
        label: 'Description',
        fields: ['variables.elements.checkout.cards.description.styles.color'],
        fontGroupBase: 'variables.elements.checkout.cards.description.styles',
      },
      {
        type: 'element',
        id: 'cards-detail',
        label: 'Detail',
        fields: ['variables.elements.checkout.cards.detail.styles.color'],
      },
    ],
  },
  'variables.elements.product.logo': {
    type: 'fieldgroup',
    id: 'logo',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Logo',
        description: 'Your brand logo at the top of the page.',
        level: 3,
        id: 'logo',
        resetable: true,
        toggleable: true,
        toggleKey: 'variables.elements.product.logo.enabled',
        resetKeys: ['variables.elements.product.logo'],
      },
      {
        type: 'conditional-wrapper',
        toggleKey: 'variables.elements.product.logo.enabled',
        children: [
          { type: 'subheader', label: 'Layout' },
          {
            type: 'columns',
            columns: 1,
            // Banner/Bubble are conditional fieldgroups rather than an "Elements"
            // subheader, so this field misses the 20px Layout/Elements gap other
            // sections get from the subheader's mt-3. mb-3 restores that rhythm.
            wrapperClassName: 'mb-3',
            fields: ['variables.elements.product.logo.hostedPageLogoStyle'],
          },
          {
            type: 'fieldgroup',
            id: 'logo-banner',
            collapsible: true,
            isOnPage: ({ values, page }) => {
              if (page?.usageType === 'site') return false;

              const style = values?.variables?.elements?.product?.logo?.hostedPageLogoStyle;
              return style === 'logoBanner' || !style;
            },
            shouldBeOpen: ({ values, page }) => {
              if (page?.usageType === 'site') return false;

              const style = values?.variables?.elements?.product?.logo?.hostedPageLogoStyle;
              return style === 'logoBanner' || !style;
            },
            divider: false,
            fields: [
              {
                type: 'heading',
                label: 'Banner',
                tooltip: 'Shown on hosted pages when hosted page logo is set to banner',
                level: 4,
                id: 'logo-banner',
                resetable: true,
                toggleable: false,
                resetKeys: ['variables.elements.product.logo.logoBanner'],
              },
              { type: 'subheader', label: 'Layout' },
              {
                type: 'columns',
                columns: 2,
                fields: [
                  {
                    path: 'variables.elements.product.logo.logoBanner.bannerStyle',
                    label: 'Style',
                  },
                  {
                    path: 'variables.elements.product.logo.logoBanner.styles.size',
                    label: 'Mark size',
                  },
                ],
              },
              { type: 'subheader', label: 'Fill' },
              {
                type: 'columns',
                columns: 1,
                fields: [
                  {
                    path: 'variables.elements.product.logo.logoBanner.styles.color',
                    label: 'Color',
                  },
                ],
              },
              { type: 'subheader', label: 'Typography' },
              {
                type: 'font-group',
                base: 'variables.elements.product.logo.logoBanner.styles',
                columns: 2,
              },
            ],
          },
          {
            type: 'fieldgroup',
            id: 'logo-bubble',
            collapsible: true,
            isOnPage: ({ values, page }) => {
              if (page?.usageType === 'site') return true;

              const style = values?.variables?.elements?.product?.logo?.hostedPageLogoStyle;
              return style === 'logoBubble';
            },
            shouldBeOpen: ({ values, page }) => {
              if (page?.usageType === 'site') return true;

              const style = values?.variables?.elements?.product?.logo?.hostedPageLogoStyle;
              return style === 'logoBubble';
            },
            divider: false,
            fields: [
              {
                type: 'heading',
                label: 'Bubble',
                tooltip:
                  'Shown on embed and pop-up pages, and on hosted pages with center layout when hosted page logo is set to bubble',
                level: 4,
                id: 'logo-bubble',
                resetable: true,
                toggleable: false,
                resetKeys: ['variables.elements.product.logo.logoBubble'],
              },
              { type: 'subheader', label: 'Layout' },
              {
                type: 'columns',
                columns: 2,
                fields: [
                  { path: 'variables.elements.product.logo.logoBubble.styles.size', label: 'Size' },
                  {
                    path: 'variables.elements.product.logo.logoBubble.styles.textAlign',
                    label: 'Alignment',
                  },
                ],
              },
              { type: 'subheader', label: 'Style' },
              'variables.elements.product.logo.logoBubble.styles.backgroundColor',
            ],
          },
        ],
      },
    ],
  },
  'variables.elements.product': {
    type: 'fieldgroup',
    id: 'product',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Product & event',
        description: 'Product title and event details like date and location.',
        level: 3,
        id: 'product',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.product.styles.textAlign',
          'variables.elements.product.title',
          'variables.elements.product.event',
          'variables.elements.product.event.date',
          'variables.elements.product.event.location',
          'variables.elements.product.event.virtual',
        ],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.product.styles.textAlign'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'product-title',
        label: 'Title',
        toggleKey: 'variables.elements.product.title.enabled',
        fields: ['variables.elements.product.title.styles.color'],
        fontGroupBase: 'variables.elements.product.title.styles',
      },
      {
        type: 'element',
        id: 'product-event-date',
        label: 'Event date',
        toggleKey: 'variables.elements.product.event.date.enabled',
        fields: ['variables.elements.product.event.date.styles.color'],
      },
      {
        type: 'element',
        id: 'product-event-location',
        label: 'Event location',
        toggleKey: 'variables.elements.product.event.location.enabled',
        fields: ['variables.elements.product.event.location.styles.color'],
      },
      {
        type: 'element',
        id: 'product-event-virtual',
        label: 'Virtual/Hybrid event',
        toggleKey: 'variables.elements.product.event.virtual.enabled',
        fields: ['variables.elements.product.event.virtual.styles.color'],
      },
    ],
  },
  // --- Price (single price block) ---
  // Styles the single/default price shown under the product. Separate from
  // "Price options" below, which styles the multi-price picker cards. Colour
  // here is free to follow the product/panel background.
  'variables.elements.product.pricing': {
    type: 'fieldgroup',
    id: 'prices',
    divider: true,
    collapsible: true,
    // The single-price block only renders when the product is NOT multi-price
    // (a multi-price product shows the picker instead → "Not on page").
    isOnPage: ({ page, product }) =>
      (page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT) &&
      !isMultiPrice(product?.prices),
    fields: [
      {
        type: 'heading',
        label: 'Price',
        description: 'Price details shown on products with a single price.',
        level: 3,
        id: 'prices',
        resetable: true,
        toggleable: true,
        resetKeys: ['variables.elements.product.pricing'],
      },
      {
        type: 'conditional-wrapper',
        toggleKey: 'variables.elements.product.pricing.enabled',
        children: [
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'pricing-badge',
            label: 'Highlight',
            hideSwatch: true,
            toggleKey: 'variables.elements.product.pricing.badge.enabled',
            fields: [
              'variables.elements.product.pricing.badge.styles.color',
              'variables.elements.product.pricing.badge.styles.backgroundColor',
              'variables.elements.product.pricing.badge.styles.borderColor',
              'variables.elements.product.pricing.badge.styles.borderWidth',
              'variables.elements.product.pricing.badge.styles.borderRadius',
              'variables.elements.product.pricing.badge.styles.padding',
            ],
            fontGroupBase: 'variables.elements.product.pricing.badge.styles',
            fieldLabels: {
              'variables.elements.product.pricing.badge.styles.color': 'Text color',
              'variables.elements.product.pricing.badge.styles.backgroundColor': 'Background',
              'variables.elements.product.pricing.badge.styles.borderColor': 'Border color',
              'variables.elements.product.pricing.badge.styles.borderWidth': 'Border width',
              'variables.elements.product.pricing.badge.styles.borderRadius': 'Border radius',
              'variables.elements.product.pricing.badge.styles.padding': 'Padding',
            },
          },
          {
            type: 'element',
            id: 'pricing-price',
            label: 'Price',
            toggleKey: 'variables.elements.product.pricing.price.enabled',
            fields: ['variables.elements.product.pricing.price.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.price.styles',
          },
          {
            type: 'element',
            id: 'pricing-interval',
            label: 'Interval',
            toggleKey: 'variables.elements.product.pricing.interval.enabled',
            fields: ['variables.elements.product.pricing.interval.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.interval.styles',
          },
          {
            type: 'element',
            id: 'pricing-discounted-from',
            label: 'Discounted from',
            toggleKey: 'variables.elements.product.pricing.discountedFromPrice.enabled',
            fields: ['variables.elements.product.pricing.discountedFromPrice.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.discountedFromPrice.styles',
          },
          {
            type: 'element',
            id: 'pricing-setup-fee',
            label: 'Setup fee',
            toggleKey: 'variables.elements.product.pricing.setupFee.enabled',
            fields: ['variables.elements.product.pricing.setupFee.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.setupFee.styles',
          },
          {
            type: 'element',
            id: 'pricing-trial-period',
            label: 'Trial period',
            toggleKey: 'variables.elements.product.pricing.trialPeriod.enabled',
            fields: ['variables.elements.product.pricing.trialPeriod.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.trialPeriod.styles',
          },
          {
            type: 'element',
            id: 'pricing-start-date',
            label: 'Start date',
            toggleKey: 'variables.elements.product.pricing.startDate.enabled',
            fields: ['variables.elements.product.pricing.startDate.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.startDate.styles',
          },
          {
            type: 'element',
            id: 'pricing-plan-iterations',
            label: 'Number of payments',
            toggleKey: 'variables.elements.product.pricing.planIterations.enabled',
            fields: ['variables.elements.product.pricing.planIterations.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.planIterations.styles',
          },
          {
            type: 'element',
            id: 'pricing-proration-renews',
            label: 'Proration renews',
            toggleKey: 'variables.elements.product.pricing.prorationRenews.enabled',
            fields: ['variables.elements.product.pricing.prorationRenews.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.prorationRenews.styles',
          },
          {
            type: 'element',
            id: 'pricing-proration-amount',
            label: 'Proration amount',
            toggleKey: 'variables.elements.product.pricing.prorationAmount.enabled',
            fields: ['variables.elements.product.pricing.prorationAmount.styles.color'],
            fontGroupBase: 'variables.elements.product.pricing.prorationAmount.styles',
          },
        ],
      },
    ],
  },
  // --- Price options (multi-price picker cards) ---
  // The selectable price cards shown when a product has multiple prices. These
  // sit on the card surface, so their per-option price/detail lines are styled
  // independently of the single-price "Price" element above (colour defaults
  // follow the card / option description, not the product/panel background).
  'variables.elements.product.pricePicker': {
    type: 'fieldgroup',
    id: 'price-options',
    divider: true,
    collapsible: true,
    // The picker only renders when the product is multi-price; a single-price
    // product shows the single price block instead → "Not on page".
    isOnPage: ({ page, product }) =>
      (page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT) &&
      isMultiPrice(product?.prices),
    fields: [
      {
        type: 'heading',
        label: 'Price options',
        description: 'Price options shown for products with multiple prices.',
        level: 3,
        id: 'price-options',
        resetable: true,
        toggleable: true,
        resetKeys: ['variables.elements.product.pricePicker'],
      },
      {
        type: 'conditional-wrapper',
        toggleKey: 'variables.elements.product.pricePicker.enabled',
        children: [
          { type: 'subheader', label: 'Layout' },
          {
            // Position spans full width above the 2×2 grid of layout dropdowns.
            type: 'columns',
            columns: 1,
            fields: ['variables.elements.product.pricePicker.position'],
          },
          {
            // Grouping | Tabs layout
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.product.pricePicker.groupBy',
              'variables.elements.product.pricePicker.groupingMode',
            ],
          },
          {
            type: 'caption',
            id: 'price-options-tabs-text-help',
            text: 'You can change the tabs text in the ',
            link: { label: 'Product', tab: 'product' },
            textAfter: ' tab.',
          },
          {
            type: 'subheader',
            label: 'Prices layout',
            id: 'price-options-group',
            divider: true,
          },
          {
            type: 'preset-picker',
            id: 'price-options-presets',
          },
          {
            // Prices layout spans full width above the alignment row.
            type: 'columns',
            columns: 1,
            fields: ['variables.elements.product.pricePicker.layout'],
          },
          {
            // Horizontal · Vertical alignment
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.product.pricePicker.horizontalAlign',
              'variables.elements.product.pricePicker.verticalAlign',
            ],
          },
          {
            // How pay-what-you-want prices render: inline input / button /
            // standalone input (picker-wide).
            type: 'columns',
            columns: 1,
            fields: ['variables.elements.product.pricePicker.pwywDisplay'],
          },
          // Section-wide text styling: every text line in the option except
          // the highlight, sitting between Cards and the per-line elements
          // (consumed via set-only mirrors -- see themePropertyRegistry).
          { type: 'subheader', label: 'Fill' },
          'variables.elements.product.pricePicker.option.styles.color',
          { type: 'subheader', label: 'Typography' },
          {
            type: 'font-group',
            base: 'variables.elements.product.pricePicker.option.styles',
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'price-options-heading',
            label: 'Heading',
            toggleKey: 'variables.elements.product.pricePicker.title.enabled',
            fields: [
              'variables.elements.product.pricePicker.title.styles.color',
              'variables.elements.product.pricePicker.title.styles.paddingBottom',
            ],
            fontGroupBase: 'variables.elements.product.pricePicker.title.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.title.styles.color': 'Text color',
              'variables.elements.product.pricePicker.title.styles.paddingBottom': 'Padding bottom',
            },
          },
          {
            type: 'element',
            id: 'price-options-heading-description',
            label: 'Heading description',
            toggleKey: 'variables.elements.product.pricePicker.description.enabled',
            fields: ['variables.elements.product.pricePicker.description.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.description.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.description.styles.color': 'Text color',
            },
          },
          {
            type: 'element',
            id: 'price-options-tabs',
            label: 'Tabs',
            hideSwatch: true,
            toggleKey: 'variables.elements.product.pricePicker.tabs.enabled',
            fields: [
              'variables.elements.product.pricePicker.tabs.styles.backgroundColor',
              'variables.elements.product.pricePicker.tabs.selectedStyles.backgroundColor',
              'variables.elements.product.pricePicker.tabs.name.styles.color',
              'variables.elements.product.pricePicker.tabs.styles.borderWidth',
              'variables.elements.product.pricePicker.tabs.styles.borderRadius',
              'variables.elements.product.pricePicker.tabs.styles.paddingBottom',
            ],
            fontGroupBase: 'variables.elements.product.pricePicker.tabs.name.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.tabs.styles.backgroundColor': 'Background',
              'variables.elements.product.pricePicker.tabs.selectedStyles.backgroundColor':
                'Selected background',
              'variables.elements.product.pricePicker.tabs.name.styles.color': 'Text color',
              'variables.elements.product.pricePicker.tabs.styles.borderWidth': 'Border width',
              'variables.elements.product.pricePicker.tabs.styles.borderRadius': 'Border radius',
              'variables.elements.product.pricePicker.tabs.styles.paddingBottom': 'Padding bottom',
            },
          },
          {
            type: 'element',
            id: 'price-options-box',
            label: 'Price option',
            hideSwatch: true,
            toggleKey: 'variables.elements.product.pricePicker.option.enabled',
            fields: [
              'variables.elements.product.pricePicker.option.styles.backgroundColor',
              'variables.elements.product.pricePicker.option.selectedStyles.backgroundColor',
              'variables.elements.product.pricePicker.option.styles.borderColor',
              'variables.elements.product.pricePicker.option.selectedStyles.borderColor',
              'variables.elements.product.pricePicker.option.styles.borderWidth',
              'variables.elements.product.pricePicker.option.styles.borderRadius',
              'variables.elements.product.pricePicker.option.styles.padding',
              'variables.elements.product.pricePicker.styles.gap',
            ],
            fieldLabels: {
              'variables.elements.product.pricePicker.option.styles.backgroundColor': 'Background',
              'variables.elements.product.pricePicker.option.selectedStyles.backgroundColor':
                'Selected background',
              'variables.elements.product.pricePicker.option.styles.borderColor': 'Border color',
              'variables.elements.product.pricePicker.option.selectedStyles.borderColor':
                'Selected border',
              'variables.elements.product.pricePicker.option.styles.borderWidth': 'Border width',
              'variables.elements.product.pricePicker.option.styles.borderRadius': 'Border radius',
              'variables.elements.product.pricePicker.option.styles.padding': 'Padding',
            },
          },
          {
            // Toggling it off hides the selection circle; the fields override the
            // ring, the unselected fill, and the selected fill+ring.
            type: 'element',
            id: 'price-options-radio',
            label: 'Check mark',
            hideSwatch: true,
            toggleKey: 'variables.elements.product.pricePicker.option.indicator.enabled',
            fields: [
              'variables.elements.product.pricePicker.option.indicator.styles.backgroundColor',
              'variables.elements.product.pricePicker.option.indicator.selectedStyles.backgroundColor',
              'variables.elements.product.pricePicker.option.indicator.styles.shape',
              'variables.elements.product.pricePicker.option.indicator.styles.color',
              'variables.elements.product.pricePicker.option.indicator.styles.borderColor',
              'variables.elements.product.pricePicker.option.indicator.selectedStyles.borderColor',
              'variables.elements.product.pricePicker.option.indicator.styles.borderWidth',
              'variables.elements.product.pricePicker.option.indicator.styles.borderRadius',
              'variables.elements.product.pricePicker.option.indicator.styles.size',
            ],
            // Shape + its color read as one concept (the selected mark), so they
            // get their own Marker eyebrow between Fill and Stroke instead of
            // the default shape→Stroke / color→Fill binning.
            fieldEyebrows: {
              'variables.elements.product.pricePicker.option.indicator.styles.shape': 'marker',
              'variables.elements.product.pricePicker.option.indicator.styles.color': 'marker',
            },
            fieldLabels: {
              'variables.elements.product.pricePicker.option.indicator.styles.color': 'Color',
              'variables.elements.product.pricePicker.option.indicator.styles.backgroundColor':
                'Background',
              'variables.elements.product.pricePicker.option.indicator.selectedStyles.backgroundColor':
                'Selected background',
              'variables.elements.product.pricePicker.option.indicator.styles.borderColor':
                'Border',
              'variables.elements.product.pricePicker.option.indicator.selectedStyles.borderColor':
                'Selected border',
              'variables.elements.product.pricePicker.option.indicator.styles.borderWidth':
                'Border width',
              'variables.elements.product.pricePicker.option.indicator.styles.borderRadius':
                'Border radius',
              'variables.elements.product.pricePicker.option.indicator.styles.shape': 'Shape',
              'variables.elements.product.pricePicker.option.indicator.styles.size': 'Size',
            },
          },
          {
            type: 'element',
            id: 'price-options-badge',
            label: 'Highlight',
            hideSwatch: true,
            toggleKey: 'variables.elements.product.pricePicker.option.badge.enabled',
            fields: [
              'variables.elements.product.pricePicker.option.badge.styles.color',
              'variables.elements.product.pricePicker.option.badge.styles.backgroundColor',
              'variables.elements.product.pricePicker.option.badge.styles.borderColor',
              'variables.elements.product.pricePicker.option.badge.styles.borderWidth',
              'variables.elements.product.pricePicker.option.badge.styles.borderRadius',
              'variables.elements.product.pricePicker.option.badge.styles.padding',
            ],
            fontGroupBase: 'variables.elements.product.pricePicker.option.badge.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.option.badge.styles.color': 'Text color',
              'variables.elements.product.pricePicker.option.badge.styles.backgroundColor':
                'Background',
              'variables.elements.product.pricePicker.option.badge.styles.borderColor':
                'Border color',
              'variables.elements.product.pricePicker.option.badge.styles.borderWidth':
                'Border width',
              'variables.elements.product.pricePicker.option.badge.styles.borderRadius':
                'Border radius',
              'variables.elements.product.pricePicker.option.badge.styles.padding': 'Padding',
            },
          },
          {
            type: 'element',
            id: 'price-options-label',
            label: 'Label',
            toggleKey: 'variables.elements.product.pricePicker.option.label.enabled',
            fields: ['variables.elements.product.pricePicker.option.label.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.label.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.option.label.styles.color': 'Text color',
            },
          },
          {
            type: 'element',
            id: 'price-options-description',
            label: 'Description',
            toggleKey: 'variables.elements.product.pricePicker.option.description.enabled',
            fields: ['variables.elements.product.pricePicker.option.description.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.description.styles',
            fieldLabels: {
              'variables.elements.product.pricePicker.option.description.styles.color':
                'Text color',
            },
          },
          {
            type: 'element',
            id: 'price-options-price',
            label: 'Price',
            toggleKey: 'variables.elements.product.pricePicker.option.price.enabled',
            fields: ['variables.elements.product.pricePicker.option.price.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.price.styles',
          },
          {
            type: 'element',
            id: 'price-options-interval',
            label: 'Interval',
            toggleKey: 'variables.elements.product.pricePicker.option.interval.enabled',
            fields: ['variables.elements.product.pricePicker.option.interval.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.interval.styles',
          },
          {
            type: 'element',
            id: 'price-options-discounted-from',
            label: 'Discounted from',
            toggleKey: 'variables.elements.product.pricePicker.option.discountedFromPrice.enabled',
            fields: [
              'variables.elements.product.pricePicker.option.discountedFromPrice.styles.color',
            ],
            fontGroupBase:
              'variables.elements.product.pricePicker.option.discountedFromPrice.styles',
          },
          {
            type: 'element',
            id: 'price-options-setup-fee',
            label: 'Setup fee',
            toggleKey: 'variables.elements.product.pricePicker.option.setupFee.enabled',
            fields: ['variables.elements.product.pricePicker.option.setupFee.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.setupFee.styles',
          },
          {
            type: 'element',
            id: 'price-options-trial-period',
            label: 'Trial period',
            toggleKey: 'variables.elements.product.pricePicker.option.trialPeriod.enabled',
            fields: ['variables.elements.product.pricePicker.option.trialPeriod.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.trialPeriod.styles',
          },
          {
            type: 'element',
            id: 'price-options-start-date',
            label: 'Start date',
            toggleKey: 'variables.elements.product.pricePicker.option.startDate.enabled',
            fields: ['variables.elements.product.pricePicker.option.startDate.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.startDate.styles',
          },
          {
            type: 'element',
            id: 'price-options-plan-iterations',
            label: 'Number of payments',
            toggleKey: 'variables.elements.product.pricePicker.option.planIterations.enabled',
            fields: ['variables.elements.product.pricePicker.option.planIterations.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.planIterations.styles',
          },
          {
            type: 'element',
            id: 'price-options-proration-renews',
            label: 'Proration renews',
            toggleKey: 'variables.elements.product.pricePicker.option.prorationRenews.enabled',
            fields: ['variables.elements.product.pricePicker.option.prorationRenews.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.prorationRenews.styles',
          },
          {
            type: 'element',
            id: 'price-options-proration-amount',
            label: 'Proration amount',
            toggleKey: 'variables.elements.product.pricePicker.option.prorationAmount.enabled',
            fields: ['variables.elements.product.pricePicker.option.prorationAmount.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.prorationAmount.styles',
          },
        ],
      },
    ],
  },
  // --- Pay what you want (unified Label + Input) ---
  // Base elements drive all four pay what you want inputs (single-price,
  // multi-price option, product variant, event ticket) until a per-context
  // subsection override is set — see pwywInputGroup/pwywLabelGroup `chainTo` in
  // themePropertyRegistry.ts. Tickets have no subsection of their own: they read
  // this base directly. Position only applies to the single-price PWYW but is
  // always shown (user decision) since it's cheap to leave visible.
  'variables.elements.checkout.pwyw': {
    type: 'fieldgroup',
    id: 'pwyw',
    divider: true,
    collapsible: true,
    isOnPage: ({ page, product }) => {
      if (page?.type !== PAGE_TYPES.CHECKOUT && page?.type !== PAGE_TYPES.EVENT) return false;
      const hasPwywPrice = (product?.prices || []).some(
        p => p.enabled !== false && p.payWhatYouWant,
      );
      // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type/payWhatYouWant)
      const legacyPwyw = product?.type === 'charge' && !!product?.payWhatYouWant;
      const hasPwywVariantOption = (product?.variants || []).some(v =>
        (v.options || []).some(o => o.type === 'pay_what_you_want' || o.payWhatYouWant === true),
      );
      // Mirrors the Ticket types section's group gate, tightened to ticket
      // types the store actually renders (TicketGroup filters on tt.status and
      // checkIsTicketTypeVisible drops hidden ones) — a disabled or hidden
      // PWYW ticket must not badge this section as on-page.
      const hasPwywTicketType = (page?.ticketGroups || []).some(
        tg =>
          tg.status === 'enabled' &&
          tg.ticketTypes?.some(
            tt => tt.status === 'enabled' && !tt.hidden && tt.pricing === 'pwyw',
          ),
      );
      return (
        hasPwywPrice ||
        legacyPwyw ||
        hasPwywVariantOption ||
        hasPwywTicketType ||
        hasPwywOrderBump(page)
      );
    },
    fields: [
      {
        type: 'heading',
        label: 'Pay what you want',
        description: 'Pay What You Want amount field for prices, variants and tickets.',
        level: 3,
        id: 'pwyw',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.pwyw',
          'variables.elements.product.pricePicker.option.pwywLabel',
          'variables.elements.product.pricePicker.option.pwywInput',
          'variables.elements.checkout.variants.variantOptions.pwywInput',
          'variables.elements.checkout.form.orderBumps.pwywInput',
          'variables.elements.checkout.form.orderBumps.pwywSuffix',
        ],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.pwyw.position'],
      },
      {
        type: 'caption',
        id: 'pwyw-position-help',
        text: 'Only applies to the single Pay What You Want amount field',
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'pwyw-label',
        label: 'Label',
        toggleKey: 'variables.elements.checkout.pwyw.label.enabled',
        fields: [
          'variables.elements.checkout.pwyw.label.styles.color',
          'variables.elements.checkout.pwyw.label.focusStyles.color',
          'variables.elements.checkout.pwyw.label.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.pwyw.label.styles',
      },
      {
        // Mirrors the PWYW input control set (see pwywInputGroup) — this base
        // drives the single, multi-price option, and variant option inputs by
        // default (see chainTo in the registry groups).
        type: 'element',
        id: 'pwyw-input',
        label: 'Input',
        hideSwatch: true,
        fields: [
          'variables.elements.checkout.pwyw.input.styles.backgroundColor',
          'variables.elements.checkout.pwyw.input.styles.color',
          'variables.elements.checkout.pwyw.input.styles.borderColor',
          'variables.elements.checkout.pwyw.input.styles.borderWidth',
          'variables.elements.checkout.pwyw.input.styles.borderRadius',
          'variables.elements.checkout.pwyw.input.styles.paddingX',
          'variables.elements.checkout.pwyw.input.styles.paddingY',
          'variables.elements.checkout.pwyw.input.styles.boxShadow',
          'variables.elements.checkout.pwyw.input.focusStyles.backgroundColor',
          'variables.elements.checkout.pwyw.input.focusStyles.color',
          'variables.elements.checkout.pwyw.input.focusStyles.borderColor',
          'variables.elements.checkout.pwyw.input.placeholderStyles.color',
        ],
        fontGroupBase: 'variables.elements.checkout.pwyw.input.styles',
      },
      {
        // The input suffix (interval "/ month" or currency code). Its default
        // follows the Input; this styles it on top.
        type: 'element',
        id: 'pwyw-interval-currency',
        label: 'Interval / Currency',
        toggleKey: 'variables.elements.checkout.pwyw.suffix.enabled',
        fields: ['variables.elements.checkout.pwyw.suffix.styles.color'],
        fontGroupBase: 'variables.elements.checkout.pwyw.suffix.styles',
      },
      {
        // Per-context override: the multi-price picker's selected PWYW option.
        // Chains its defaults to the base Label/Input above.
        type: 'fieldgroup',
        id: 'pwyw-price-options',
        collapsible: true,
        divider: false,
        className: 'mt-3 first:mt-0',
        isOnPage: ({ page, product }) =>
          (page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT) &&
          isMultiPrice(product?.prices) &&
          (product?.prices || []).some(p => p.enabled !== false && p.payWhatYouWant),
        fields: [
          {
            type: 'heading',
            label: 'Price options PWYW',
            description: 'Overrides for the pay what you want amount field in a price option.',
            level: 4,
            id: 'pwyw-price-options',
            resetable: true,
            toggleable: false,
            resetKeys: [
              'variables.elements.product.pricePicker.option.pwywLabel',
              'variables.elements.product.pricePicker.option.pwywInput',
              'variables.elements.product.pricePicker.option.pwywSuffix',
            ],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'price-options-pwyw-label',
            label: 'Label',
            fields: [
              'variables.elements.product.pricePicker.option.pwywLabel.styles.color',
              'variables.elements.product.pricePicker.option.pwywLabel.focusStyles.color',
              'variables.elements.product.pricePicker.option.pwywLabel.styles.paddingBottom',
            ],
            fontGroupBase: 'variables.elements.product.pricePicker.option.pwywLabel.styles',
          },
          {
            type: 'element',
            id: 'price-options-pwyw-input',
            label: 'Input',
            hideSwatch: true,
            fields: [
              'variables.elements.product.pricePicker.option.pwywInput.styles.backgroundColor',
              'variables.elements.product.pricePicker.option.pwywInput.styles.color',
              'variables.elements.product.pricePicker.option.pwywInput.styles.borderColor',
              'variables.elements.product.pricePicker.option.pwywInput.styles.borderWidth',
              'variables.elements.product.pricePicker.option.pwywInput.styles.borderRadius',
              'variables.elements.product.pricePicker.option.pwywInput.styles.paddingX',
              'variables.elements.product.pricePicker.option.pwywInput.styles.paddingY',
              'variables.elements.product.pricePicker.option.pwywInput.styles.boxShadow',
              'variables.elements.product.pricePicker.option.pwywInput.focusStyles.backgroundColor',
              'variables.elements.product.pricePicker.option.pwywInput.focusStyles.color',
              'variables.elements.product.pricePicker.option.pwywInput.focusStyles.borderColor',
              'variables.elements.product.pricePicker.option.pwywInput.placeholderStyles.color',
            ],
            fontGroupBase: 'variables.elements.product.pricePicker.option.pwywInput.styles',
          },
          {
            type: 'element',
            id: 'price-options-pwyw-interval-currency',
            label: 'Interval / Currency',
            toggleKey: 'variables.elements.product.pricePicker.option.pwywSuffix.enabled',
            fields: ['variables.elements.product.pricePicker.option.pwywSuffix.styles.color'],
            fontGroupBase: 'variables.elements.product.pricePicker.option.pwywSuffix.styles',
          },
        ],
      },
      {
        // Per-context override: a variant option's PWYW amount box. No Label
        // row — variant options render no label for their PWYW input.
        type: 'fieldgroup',
        id: 'pwyw-variants',
        collapsible: true,
        divider: false,
        isOnPage: ({ page, product }) =>
          page?.type === PAGE_TYPES.CHECKOUT &&
          (product?.variants || []).some(v =>
            (v.options || []).some(
              o => o.type === 'pay_what_you_want' || o.payWhatYouWant === true,
            ),
          ),
        fields: [
          {
            type: 'heading',
            label: 'Product variants PWYW',
            description: 'Overrides for the pay what you want amount field in a product variant.',
            level: 4,
            id: 'pwyw-variants',
            resetable: true,
            toggleable: false,
            resetKeys: [
              'variables.elements.checkout.variants.variantOptions.pwywInput',
              'variables.elements.checkout.variants.variantOptions.pwywSuffix',
            ],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'variants-option-pwyw-input',
            label: 'Input',
            hideSwatch: true,
            fields: [
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.backgroundColor',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.color',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.borderColor',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.borderWidth',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.borderRadius',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.paddingX',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.paddingY',
              'variables.elements.checkout.variants.variantOptions.pwywInput.styles.boxShadow',
              'variables.elements.checkout.variants.variantOptions.pwywInput.focusStyles.backgroundColor',
              'variables.elements.checkout.variants.variantOptions.pwywInput.focusStyles.color',
              'variables.elements.checkout.variants.variantOptions.pwywInput.focusStyles.borderColor',
              'variables.elements.checkout.variants.variantOptions.pwywInput.placeholderStyles.color',
            ],
            fontGroupBase: 'variables.elements.checkout.variants.variantOptions.pwywInput.styles',
          },
          {
            type: 'element',
            id: 'variants-option-pwyw-interval-currency',
            label: 'Interval / Currency',
            toggleKey: 'variables.elements.checkout.variants.variantOptions.pwywSuffix.enabled',
            fields: ['variables.elements.checkout.variants.variantOptions.pwywSuffix.styles.color'],
            fontGroupBase: 'variables.elements.checkout.variants.variantOptions.pwywSuffix.styles',
          },
        ],
      },
      {
        // Per-context override: the amount box on a pay-what-you-want order
        // bump. No Label row — the bump's title stands in for one.
        type: 'fieldgroup',
        id: 'pwyw-order-bumps',
        collapsible: true,
        divider: false,
        isOnPage: ({ page }) => hasPwywOrderBump(page),
        fields: [
          {
            type: 'heading',
            label: 'Bumps / Add-ons PWYW',
            description:
              'Overrides for the pay what you want amount field in an order bump or add-on.',
            level: 4,
            id: 'pwyw-order-bumps',
            resetable: true,
            toggleable: false,
            resetKeys: [
              'variables.elements.checkout.form.orderBumps.pwywInput',
              'variables.elements.checkout.form.orderBumps.pwywSuffix',
            ],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'order-bumps-pwyw-input',
            label: 'Input',
            hideSwatch: true,
            fields: [
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.color',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.paddingX',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.paddingY',
              'variables.elements.checkout.form.orderBumps.pwywInput.styles.boxShadow',
              'variables.elements.checkout.form.orderBumps.pwywInput.focusStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.pwywInput.focusStyles.color',
              'variables.elements.checkout.form.orderBumps.pwywInput.focusStyles.borderColor',
              'variables.elements.checkout.form.orderBumps.pwywInput.placeholderStyles.color',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.pwywInput.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-pwyw-interval-currency',
            label: 'Interval / Currency',
            toggleKey: 'variables.elements.checkout.form.orderBumps.pwywSuffix.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.pwywSuffix.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.pwywSuffix.styles',
          },
        ],
      },
    ],
  },
  'variables.elements.description': {
    type: 'fieldgroup',
    id: 'description',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Description',
        description:
          'Headings, body, links, quotes, lists, and accordions in the product description.',
        level: 3,
        id: 'description',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.description'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'description-headings',
        label: 'Headings',
        fields: ['variables.elements.description.headings.styles.color'],
      },
      {
        type: 'element',
        id: 'description-body',
        label: 'Body text',
        fields: ['variables.elements.description.body.styles.color'],
      },
      {
        type: 'element',
        id: 'description-links',
        label: 'Links',
        fields: ['variables.elements.description.links.styles.color'],
      },
      {
        type: 'element',
        id: 'description-quotes',
        label: 'Quotes',
        fields: ['variables.elements.description.quotes.styles.color'],
      },
      {
        type: 'element',
        id: 'description-bullets',
        label: 'Bulleted lists',
        fields: [
          'variables.elements.description.bullets.styles.color',
          'variables.elements.description.bullets.type',
          'variables.elements.description.bullets.styles.fontSize',
          'variables.elements.description.bullets.align',
        ],
      },
      {
        type: 'element',
        id: 'description-collapsible-title',
        label: 'Accordion title',
        fields: ['variables.elements.description.collapsibles.title.styles.color'],
      },
      {
        type: 'element',
        id: 'description-collapsible-body',
        label: 'Accordion body',
        fields: ['variables.elements.description.collapsibles.body.styles.color'],
      },
      {
        type: 'element',
        id: 'description-collapsible-icon',
        label: 'Accordion icon',
        fields: [
          'variables.elements.description.collapsibles.icon.styles.color',
          'variables.elements.description.collapsibles.icon.type',
        ],
      },
      {
        type: 'element',
        id: 'description-collapsible-border',
        label: 'Accordion border',
        fields: [
          'variables.elements.description.collapsibles.border.styles.borderColor',
          'variables.elements.description.collapsibles.border.styles.borderWidth',
          'variables.elements.description.collapsibles.border.styles.borderRadius',
        ],
      },
    ],
  },
  'variables.elements.checkout.variants': {
    type: 'fieldgroup',
    id: 'variants',
    divider: true,
    collapsible: true,
    isOnPage: ({ page, product }) =>
      page?.type === PAGE_TYPES.CHECKOUT && product?.variants?.length > 0,
    fields: [
      {
        type: 'heading',
        label: 'Product variants',
        description: 'Variant selector shown next to the product.',
        level: 3,
        id: 'variants',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.variants'],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.variants.position'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'variants-variant',
        label: 'Variant',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.variants.variantOptions.styles.backgroundColor',
          'variables.elements.checkout.variants.variantOptions.selectedStyles.backgroundColor',
          'variables.elements.checkout.variants.variantOptions.styles.borderColor',
          'variables.elements.checkout.variants.variantOptions.selectedStyles.borderColor',
          'variables.elements.checkout.variants.variantOptions.styles.borderWidth',
          'variables.elements.checkout.variants.variantOptions.styles.borderRadius',
        ],
        fieldLabels: {
          'variables.elements.checkout.variants.variantOptions.styles.borderColor': 'Border color',
          'variables.elements.checkout.variants.variantOptions.styles.borderWidth': 'Border width',
          'variables.elements.checkout.variants.variantOptions.styles.borderRadius':
            'Border radius',
        },
      },
      {
        type: 'element',
        id: 'variants-name',
        label: 'Variant name',
        toggleKey: 'variables.elements.checkout.variants.name.enabled',
        fields: [
          'variables.elements.checkout.variants.name.styles.color',
          'variables.elements.checkout.variants.name.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.variants.name.styles',
      },
      {
        type: 'element',
        id: 'variants-option-name',
        label: 'Option name',
        toggleKey: 'variables.elements.checkout.variants.variantOptions.name.enabled',
        fields: ['variables.elements.checkout.variants.variantOptions.name.styles.color'],
        fontGroupBase: 'variables.elements.checkout.variants.variantOptions.name.styles',
      },
      {
        type: 'element',
        id: 'variants-option-price',
        label: 'Price',
        toggleKey: 'variables.elements.checkout.variants.variantOptions.price.enabled',
        fields: ['variables.elements.checkout.variants.variantOptions.price.styles.color'],
        fontGroupBase: 'variables.elements.checkout.variants.variantOptions.price.styles',
      },
      {
        type: 'element',
        id: 'variants-option-discounted-from-price',
        label: 'Discounted from price',
        toggleKey:
          'variables.elements.checkout.variants.variantOptions.discountedFromPrice.enabled',
        fields: [
          'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.color',
        ],
        fontGroupBase:
          'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles',
      },
      {
        type: 'element',
        id: 'variants-option-plan-iterations',
        label: 'Number of payments',
        toggleKey: 'variables.elements.checkout.variants.variantOptions.planIterations.enabled',
        fields: ['variables.elements.checkout.variants.variantOptions.planIterations.styles.color'],
        fontGroupBase: 'variables.elements.checkout.variants.variantOptions.planIterations.styles',
      },
      {
        type: 'element',
        id: 'variants-option-description',
        label: 'Description',
        toggleKey: 'variables.elements.checkout.variants.variantOptions.description.enabled',
        fields: ['variables.elements.checkout.variants.variantOptions.description.styles.color'],
        fontGroupBase: 'variables.elements.checkout.variants.variantOptions.description.styles',
      },
      {
        type: 'element',
        id: 'variants-option-plus-sign',
        label: 'Plus sign',
        toggleKey: 'variables.elements.checkout.variants.variantOptions.plusSign.enabled',
        fields: ['variables.elements.checkout.variants.variantOptions.plusSign.styles.color'],
      },
    ],
  },
  'variables.elements.checkout.form.orderBumps': {
    type: 'fieldgroup',
    id: 'order-bumps',
    divider: true,
    collapsible: true,
    isOnPage: ({ page }) => carriesOrderBumps(page) && page?.orderBumps?.length > 0,
    fields: [
      {
        type: 'heading',
        label: 'Bumps / Add-ons',
        description: 'Add-on offers the customer can accept with one click.',
        level: 3,
        id: 'order-bumps',
        resetable: true,
        toggleable: true,
        toggleKey: 'variables.elements.checkout.form.orderBumps.enabled',
        resetKeys: ['variables.elements.checkout.form.orderBumps'],
      },
      {
        type: 'conditional-wrapper',
        toggleKey: 'variables.elements.checkout.form.orderBumps.enabled',
        children: [
          // Layout is where the cards SIT: which column, and whether they run
          // down the page or across it. How one card arranges the offer inside
          // itself is a style, and lives under its own heading below.
          { type: 'subheader', label: 'Layout' },
          {
            // Position | List or grid
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.checkout.form.orderBumps.position',
              'variables.elements.checkout.form.orderBumps.layout',
            ],
          },
          // A preset is the whole card — the arrangement AND the call to
          // action — because the call to action is what makes a card read as
          // an order bump. The fields hang off it as one group, card first
          // and call to action after: they are where a seller diverges from
          // the chip, not two subjects of their own.
          {
            type: 'subheader',
            label: 'Style',
            id: 'order-bumps-style',
          },
          {
            type: 'preset-picker',
            id: 'order-bump-presets',
          },
          {
            // What the call to action sits on | where that sits on the card.
            // The layout leads because it is the bigger decision: it settles
            // whether the call to action gets a line of its own at all, and
            // which positions can then mean anything.
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.checkout.form.orderBumps.acceptAppearance',
              {
                path: 'variables.elements.checkout.form.orderBumps.acceptPosition',
                // Beside the offer the control takes a column at one side, so
                // the top centre means nothing. The bottom is always a line of
                // its own, so the centre still means something there. A row or
                // bar lies above or below the offer and has no middle edge.
                options: (options, { values }) =>
                  isCallToActionBesideOffer(values)
                    ? options.filter(option => option.value !== 'top-center')
                    : options.filter(option => !isMiddleEdge(option)),
              },
            ],
          },
          {
            // What the buyer taps | how the card's content sits. The alignment
            // is the one choice that reshapes the whole card, control and copy
            // alike, so it rides with the control rather than with the image.
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.checkout.form.orderBumps.acceptStyle',
              'variables.elements.checkout.form.orderBumps.horizontalAlign',
            ],
          },
          {
            // Where the image sits | how much of the row it takes. The size is
            // the card's column split, so it belongs beside the placement
            // rather than down in the image's own styling.
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.checkout.form.orderBumps.mediaPosition',
              'variables.elements.checkout.form.orderBumps.image.styles.size',
            ],
          },
          {
            // The image's shape | where the highlight sits against the title.
            // Beside the selector the highlight read as the control's own
            // setting, which it is not.
            type: 'columns',
            columns: 2,
            fields: [
              'variables.elements.checkout.form.orderBumps.image.styles.aspectRatio',
              {
                path: 'variables.elements.checkout.form.orderBumps.badge.position',
                // Centred copy has no row end for the highlight to sit at: the
                // card stacks it whatever the field says, so the field says so.
                options: (options, { values }) =>
                  isCardCentered(values)
                    ? options.filter(option => option.value === 'above')
                    : options,
              },
            ],
          },
          {
            // On its own, but in a half-width cell so every dropdown in the
            // group is the same size.
            type: 'columns',
            columns: 2,
            fields: ['variables.elements.checkout.form.orderBumps.descriptionPosition'],
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'order-bumps-card',
            label: 'Order bump',
            hideSwatch: true,
            showEye: false,
            fields: [
              'variables.elements.checkout.form.orderBumps.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.selectedStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.selectedStyles.borderColor',
              'variables.elements.checkout.form.orderBumps.styles.borderStyle',
              'variables.elements.checkout.form.orderBumps.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.styles.borderRadius',
            ],
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.styles.borderColor': 'Border color',
              'variables.elements.checkout.form.orderBumps.styles.borderStyle': 'Border style',
              'variables.elements.checkout.form.orderBumps.styles.borderWidth': 'Border width',
              'variables.elements.checkout.form.orderBumps.styles.borderRadius': 'Border radius',
            },
          },
          {
            // The box and the copy on it are one thing to a seller — a call to
            // action that is a surface, a border and a line of type — so they
            // are one row here. The box half is read only by the appearances
            // that give the call to action a surface: beside the offer there
            // is none.
            type: 'element',
            id: 'order-bumps-call-to-action',
            label: 'Call to action',
            toggleKey: 'variables.elements.checkout.form.orderBumps.callToAction.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.callToAction.styles.color',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.borderColor',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.padding',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.dividerWidth',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.callToAction.styles',
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.callToAction.styles.color': 'Text',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.backgroundColor':
                'Background',
              'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.backgroundColor':
                'Selected background',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderColor':
                'Border color',
              'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.borderColor':
                'Selected border color',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderWidth':
                'Border width',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderRadius':
                'Border radius',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.padding': 'Padding',
              'variables.elements.checkout.form.orderBumps.acceptBar.styles.dividerWidth':
                'Divider',
            },
          },
          // One element per control the Selector dropdown can put on the
          // card, all four always listed, in the dropdown's order. Sharing one
          // element meant Button showed a Shape field and Mark a Button
          // padding, and a value set for one control dressed the next. None of
          // the four has an eye: the selector is what the buyer taps, so a
          // card with it hidden would have nothing to press.
          {
            type: 'element',
            id: 'order-bumps-switch',
            label: 'Switch',
            hideSwatch: true,
            fields: [
              'variables.elements.checkout.form.orderBumps.switch.styles.size',
              'variables.elements.checkout.form.orderBumps.switch.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.switch.selectedStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.switch.styles.color',
            ],
          },
          {
            type: 'element',
            id: 'order-bumps-mark',
            label: 'Mark',
            // Shape and radius are what separate a radio from a checkbox, so
            // they lead.
            fields: [
              'variables.elements.checkout.form.orderBumps.indicator.styles.shape',
              'variables.elements.checkout.form.orderBumps.indicator.styles.size',
              'variables.elements.checkout.form.orderBumps.indicator.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.indicator.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.indicator.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.indicator.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.borderColor',
              'variables.elements.checkout.form.orderBumps.indicator.styles.color',
            ],
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.indicator.styles.backgroundColor':
                'Background',
              'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.backgroundColor':
                'Selected background',
              'variables.elements.checkout.form.orderBumps.indicator.styles.borderColor': 'Border',
              'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.borderColor':
                'Selected border',
              'variables.elements.checkout.form.orderBumps.indicator.styles.color': 'Mark',
            },
          },
          {
            // The copy on the button and its font are the call to action's,
            // above; this is the box, and the colour of the copy on it.
            type: 'element',
            id: 'order-bumps-accept-button',
            label: 'Button',
            hideSwatch: true,
            fields: [
              'variables.elements.checkout.form.orderBumps.acceptButton.fill',
              'variables.elements.checkout.form.orderBumps.acceptButton.width',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingX',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingY',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.color',
              'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.color',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.borderColor',
            ],
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.acceptButton.fill': 'Fill',
              'variables.elements.checkout.form.orderBumps.acceptButton.width': 'Width',
            },
          },
          {
            // The call to action's copy as a link: its colour and type, and
            // whether it carries the underline. Unset, each follows the Call
            // to action element. The resting colour is the header swatch,
            // as on the copy elements, and Selected text stays labelled
            // beneath it; the underline is part of the type, so it sits with
            // the font rather than in Layout, where a select lands.
            type: 'element',
            id: 'order-bumps-link',
            label: 'Link',
            fields: [
              'variables.elements.checkout.form.orderBumps.link.styles.color',
              'variables.elements.checkout.form.orderBumps.link.selectedStyles.color',
              'variables.elements.checkout.form.orderBumps.link.styles.textDecoration',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.link.styles',
            fieldEyebrows: {
              'variables.elements.checkout.form.orderBumps.link.styles.textDecoration':
                'typography',
            },
          },
          {
            // The seller's line above the card. Unset it follows the Title
            // element, a step heavier; the padding is the gap to the card.
            type: 'element',
            id: 'order-bumps-heading',
            label: 'Heading',
            toggleKey: 'variables.elements.checkout.form.orderBumps.heading.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.heading.styles.color',
              'variables.elements.checkout.form.orderBumps.heading.styles.paddingBottom',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.heading.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-badge',
            label: 'Highlight',
            hideSwatch: true,
            toggleKey: 'variables.elements.checkout.form.orderBumps.badge.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.badge.styles.color',
              'variables.elements.checkout.form.orderBumps.badge.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.badge.styles.padding',
              'variables.elements.checkout.form.orderBumps.badge.icon.styles.color',
              'variables.elements.checkout.form.orderBumps.badge.icon.styles.size',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.badge.styles',
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.badge.styles.color': 'Text color',
              'variables.elements.checkout.form.orderBumps.badge.icon.styles.color': 'Icon color',
              'variables.elements.checkout.form.orderBumps.badge.icon.styles.size': 'Icon size',
              'variables.elements.checkout.form.orderBumps.badge.styles.backgroundColor':
                'Background',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderColor':
                'Border color',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderWidth':
                'Border width',
              'variables.elements.checkout.form.orderBumps.badge.styles.borderRadius':
                'Border radius',
              'variables.elements.checkout.form.orderBumps.badge.styles.padding': 'Padding',
            },
          },
          {
            type: 'element',
            id: 'order-bumps-title',
            label: 'Title',
            toggleKey: 'variables.elements.checkout.form.orderBumps.title.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.title.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.title.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-short-description',
            label: 'Short description',
            toggleKey: 'variables.elements.checkout.form.orderBumps.shortDescription.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.shortDescription.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.shortDescription.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-price',
            label: 'Price',
            toggleKey: 'variables.elements.checkout.form.orderBumps.price.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.price.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.price.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-discounted-from-price',
            label: 'Discounted from',
            toggleKey: 'variables.elements.checkout.form.orderBumps.discountedFromPrice.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles.color',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles',
          },
          {
            type: 'element',
            id: 'order-bumps-setup-fee',
            label: 'Setup fee',
            toggleKey: 'variables.elements.checkout.form.orderBumps.setupFee.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.setupFee.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.setupFee.styles',
          },
          {
            // The "what you get" list above the description: its text in the
            // header swatch, the icons' own colour beneath, and the list's
            // shape (list or grid, spacing, icon size) under Layout.
            type: 'element',
            id: 'order-bumps-features',
            label: 'Features',
            toggleKey: 'variables.elements.checkout.form.orderBumps.features.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.features.styles.color',
              'variables.elements.checkout.form.orderBumps.features.icon.styles.color',
              'variables.elements.checkout.form.orderBumps.features.layout',
              'variables.elements.checkout.form.orderBumps.features.styles.gap',
              'variables.elements.checkout.form.orderBumps.features.icon.styles.size',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.features.styles',
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.features.styles.color': 'Text',
              'variables.elements.checkout.form.orderBumps.features.icon.styles.color': 'Icon',
              'variables.elements.checkout.form.orderBumps.features.icon.styles.size': 'Icon size',
            },
          },
          {
            // The call out box under the description: its colours in the
            // swatches, the box's shape beneath them.
            type: 'element',
            id: 'order-bumps-callout',
            label: 'Call out',
            hideSwatch: true,
            toggleKey: 'variables.elements.checkout.form.orderBumps.callout.enabled',
            fields: [
              'variables.elements.checkout.form.orderBumps.callout.styles.color',
              'variables.elements.checkout.form.orderBumps.callout.styles.backgroundColor',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderColor',
              'variables.elements.checkout.form.orderBumps.callout.icon.styles.color',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderWidth',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderRadius',
              'variables.elements.checkout.form.orderBumps.callout.styles.padding',
              'variables.elements.checkout.form.orderBumps.callout.icon.styles.size',
            ],
            fontGroupBase: 'variables.elements.checkout.form.orderBumps.callout.styles',
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.callout.styles.color': 'Text color',
              'variables.elements.checkout.form.orderBumps.callout.styles.backgroundColor':
                'Background',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderColor':
                'Border color',
              'variables.elements.checkout.form.orderBumps.callout.icon.styles.color': 'Icon color',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderWidth':
                'Border width',
              'variables.elements.checkout.form.orderBumps.callout.styles.borderRadius':
                'Border radius',
              'variables.elements.checkout.form.orderBumps.callout.styles.padding': 'Padding',
              'variables.elements.checkout.form.orderBumps.callout.icon.styles.size': 'Icon size',
            },
          },
          {
            type: 'element',
            id: 'order-bumps-image',
            label: 'Image',
            hideSwatch: true,
            toggleKey: 'variables.elements.checkout.form.orderBumps.image.enabled',
            fields: ['variables.elements.checkout.form.orderBumps.image.styles.borderRadius'],
            fieldLabels: {
              'variables.elements.checkout.form.orderBumps.image.styles.borderRadius':
                'Border radius',
            },
          },
        ],
      },
    ],
  },

  'variables.elements.checkout.ticketGroups': {
    type: 'fieldgroup',
    id: 'ticket-types',
    divider: true,
    collapsible: true,
    isOnPage: ({ page }) =>
      page?.type === PAGE_TYPES.EVENT &&
      page?.ticketGroups?.some(tg => tg.status === 'enabled' && tg.ticketTypes?.length > 0),
    fields: [
      {
        type: 'heading',
        label: 'Ticket types',
        description: 'Ticket selection on event pages.',
        level: 3,
        id: 'ticket-types',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.ticketGroups'],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.ticketGroups.position'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'ticket-types-ticket',
        label: 'Ticket',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.ticketGroups.ticketTypes.quantitySelector',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.backgroundColor',
          'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.backgroundColor',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderColor',
          'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.borderColor',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderWidth',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderRadius',
        ],
        fieldEyebrows: {
          'variables.elements.checkout.ticketGroups.ticketTypes.quantitySelector': 'quantity',
        },
        fieldLabels: {
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderColor': 'Border color',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderWidth': 'Border width',
          'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderRadius':
            'Border radius',
        },
      },
      {
        type: 'element',
        id: 'ticket-types-group-name',
        label: 'Group name',
        toggleKey: 'variables.elements.checkout.ticketGroups.name.enabled',
        fields: [
          'variables.elements.checkout.ticketGroups.name.styles.color',
          'variables.elements.checkout.ticketGroups.name.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.name.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-group-description',
        label: 'Group description',
        toggleKey: 'variables.elements.checkout.ticketGroups.description.enabled',
        fields: ['variables.elements.checkout.ticketGroups.description.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.description.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-name',
        label: 'Name',
        toggleKey: 'variables.elements.checkout.ticketGroups.ticketTypes.name.enabled',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.name.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-price',
        label: 'Price',
        toggleKey: 'variables.elements.checkout.ticketGroups.ticketTypes.price.enabled',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.price.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-discounted-from-price',
        label: 'Discounted from',
        toggleKey:
          'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.enabled',
        fields: [
          'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.color',
        ],
        fontGroupBase:
          'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-booking-fee',
        label: 'Booking fee',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-remaining',
        label: 'Remaining',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-sale-date',
        label: 'Sale date',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-total',
        label: 'Total price',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.total.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles',
      },
      {
        type: 'element',
        id: 'ticket-types-description',
        label: 'Description',
        toggleKey: 'variables.elements.checkout.ticketGroups.ticketTypes.description.enabled',
        fields: ['variables.elements.checkout.ticketGroups.ticketTypes.description.styles.color'],
        fontGroupBase: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles',
      },
    ],
  },
  'variables.elements.checkout.statusBadges': {
    type: 'fieldgroup',
    id: 'statusBadges',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Badges',
        description: 'Status labels like sold out, canceled, and unavailable.',
        level: 3,
        id: 'statusBadges',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.statusBadges'],
      },
      { type: 'subheader', label: 'Typography' },
      {
        type: 'font-group',
        base: 'variables.elements.checkout.statusBadges.styles',
        columns: 2,
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'badges-sold-out',
        label: 'Sold out',
        variant: 'badge',
        fields: [
          'variables.elements.checkout.statusBadges.soldOutColor',
          'variables.elements.checkout.statusBadges.soldOutBackgroundColor',
        ],
      },
      {
        type: 'element',
        id: 'badges-canceled',
        label: 'Canceled',
        variant: 'badge',
        fields: [
          'variables.elements.checkout.statusBadges.canceledColor',
          'variables.elements.checkout.statusBadges.canceledBackgroundColor',
        ],
      },
      {
        type: 'element',
        id: 'badges-unavailable',
        label: 'Unavailable',
        variant: 'badge',
        fields: [
          'variables.elements.checkout.statusBadges.unavailableColor',
          'variables.elements.checkout.statusBadges.unavailableBackgroundColor',
        ],
      },
      {
        type: 'element',
        id: 'badges-on-sale-soon',
        label: 'On sale soon',
        variant: 'badge',
        fields: [
          'variables.elements.checkout.statusBadges.onSaleSoonColor',
          'variables.elements.checkout.statusBadges.onSaleSoonBackgroundColor',
        ],
      },
      {
        type: 'element',
        id: 'badges-not-on-sale',
        label: 'Not on sale',
        variant: 'badge',
        fields: [
          'variables.elements.checkout.statusBadges.notOnSaleColor',
          'variables.elements.checkout.statusBadges.notOnSaleBackgroundColor',
        ],
      },
    ],
  },
  'variables.elements.checkout.form.fields': {
    type: 'fieldgroup',
    id: 'fields',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Fields',
        description:
          'Groups, headings, dividers, labels, inputs, helper text and the discount code in form fields.',
        level: 3,
        id: 'fields',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.form.fields',
          'variables.elements.checkout.form.groups',
          'variables.elements.checkout.form.heading',
          'variables.elements.checkout.form.subheading',
          'variables.elements.checkout.form.divider',
          'variables.elements.checkout.form.discountCode',
        ],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'fields-groups',
        label: 'Groups',
        hideSwatch: true,
        showEye: false,
        resetKeys: ['variables.elements.checkout.form.groups'],
        fields: [
          'variables.elements.checkout.form.groups.styles.gap',
          'variables.elements.checkout.form.groups.styles.rowGap',
          'variables.elements.checkout.form.groups.phones',
          'variables.elements.checkout.form.groups.styles.alignItems',
        ],
      },
      {
        type: 'element',
        id: 'fields-heading',
        label: 'Heading',
        toggleKey: 'variables.elements.checkout.form.heading.enabled',
        resetKeys: ['variables.elements.checkout.form.heading'],
        fields: [
          'variables.elements.checkout.form.heading.styles.color',
          'variables.elements.checkout.form.heading.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.form.heading.styles',
      },
      {
        type: 'element',
        id: 'fields-subheading',
        label: 'Subheading',
        toggleKey: 'variables.elements.checkout.form.subheading.enabled',
        resetKeys: ['variables.elements.checkout.form.subheading'],
        fields: [
          'variables.elements.checkout.form.subheading.styles.color',
          'variables.elements.checkout.form.subheading.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.form.subheading.styles',
      },
      {
        type: 'element',
        id: 'fields-divider',
        label: 'Divider',
        toggleKey: 'variables.elements.checkout.form.divider.enabled',
        resetKeys: ['variables.elements.checkout.form.divider'],
        fields: [
          'variables.elements.checkout.form.divider.styles.borderColor',
          'variables.elements.checkout.form.divider.styles.borderWidth',
          'variables.elements.checkout.form.divider.styles.borderStyle',
          'variables.elements.checkout.form.divider.styles.width',
          'variables.elements.checkout.form.divider.styles.marginTop',
          'variables.elements.checkout.form.divider.styles.marginBottom',
        ],
      },
      {
        type: 'element',
        id: 'fields-label',
        label: 'Label',
        toggleKey: 'variables.elements.checkout.form.fields.label.enabled',
        resetKeys: ['variables.elements.checkout.form.fields.label'],
        fields: [
          'variables.elements.checkout.form.fields.label.styles.color',
          'variables.elements.checkout.form.fields.label.focusStyles.color',
          'variables.elements.checkout.form.fields.label.styles.paddingBottom',
        ],
        fontGroupBase: 'variables.elements.checkout.form.fields.label.styles',
      },
      {
        type: 'element',
        id: 'fields-input',
        label: 'Input',
        hideSwatch: true,
        showEye: false,
        resetKeys: ['variables.elements.checkout.form.fields.input'],
        fieldToggleKeys: {
          'variables.elements.checkout.form.fields.input.placeholderStyles.color':
            'variables.elements.checkout.form.fields.input.placeholder.enabled',
        },
        fields: [
          'variables.elements.checkout.form.fields.input.styles.color',
          'variables.elements.checkout.form.fields.input.styles.backgroundColor',
          'variables.elements.checkout.form.fields.input.placeholderStyles.color',
          'variables.elements.checkout.form.fields.input.focusStyles.color',
          'variables.elements.checkout.form.fields.input.focusStyles.backgroundColor',
          'variables.elements.checkout.form.fields.input.styles.borderColor',
          'variables.elements.checkout.form.fields.input.focusStyles.borderColor',
          'variables.elements.checkout.form.fields.input.styles.borderWidth',
          'variables.elements.checkout.form.fields.input.styles.borderRadius',
          'variables.elements.checkout.form.fields.input.styles.paddingX',
          'variables.elements.checkout.form.fields.input.styles.paddingY',
          'variables.elements.checkout.form.fields.input.styles.boxShadow',
        ],
        fieldLabels: {
          'variables.elements.checkout.form.fields.input.styles.color': 'Text color',
          'variables.elements.checkout.form.fields.input.focusStyles.color': 'Text focus color',
          'variables.elements.checkout.form.fields.input.placeholderStyles.color':
            'Placeholder color',
          'variables.elements.checkout.form.fields.input.styles.borderColor': 'Border color',
          'variables.elements.checkout.form.fields.input.focusStyles.borderColor':
            'Border focus color',
          'variables.elements.checkout.form.fields.input.styles.borderWidth': 'Border width',
          'variables.elements.checkout.form.fields.input.styles.borderRadius': 'Border radius',
          'variables.elements.checkout.form.fields.input.styles.boxShadow': 'Shadow width',
        },
        fontGroupBase: 'variables.elements.checkout.form.fields.input.styles',
      },
      {
        // Every − / + stepper (tickets, bumps, variants, the quantity field). The
        // count box follows Input until set; focus always uses Input's.
        type: 'element',
        id: 'fields-quantity-selector',
        label: 'Quantity selector',
        hideSwatch: true,
        showEye: false,
        resetKeys: ['variables.elements.checkout.form.fields.quantitySelector'],
        fields: [
          'variables.elements.checkout.form.fields.quantitySelector.icon.styles.color',
          'variables.elements.checkout.form.fields.quantitySelector.icon.styles.size',
          'variables.elements.checkout.form.fields.quantitySelector.styles.backgroundColor',
          'variables.elements.checkout.form.fields.quantitySelector.styles.color',
          'variables.elements.checkout.form.fields.quantitySelector.styles.borderColor',
          'variables.elements.checkout.form.fields.quantitySelector.styles.borderWidth',
          'variables.elements.checkout.form.fields.quantitySelector.styles.borderRadius',
        ],
        fieldEyebrows: {
          'variables.elements.checkout.form.fields.quantitySelector.icon.styles.color': 'icon',
          'variables.elements.checkout.form.fields.quantitySelector.icon.styles.size': 'icon',
        },
      },
      {
        type: 'element',
        id: 'fields-description',
        label: 'Description',
        toggleKey: 'variables.elements.checkout.form.fields.description.enabled',
        resetKeys: ['variables.elements.checkout.form.fields.description'],
        fields: [
          'variables.elements.checkout.form.fields.description.styles.color',
          'variables.elements.checkout.form.fields.description.styles.paddingTop',
        ],
        fontGroupBase: 'variables.elements.checkout.form.fields.description.styles',
      },
      {
        type: 'element',
        id: 'discount-link',
        isAvailable: ({ page }) => page?.type !== 'form',
        label: 'Discount code link',
        resetKeys: ['variables.elements.checkout.form.discountCode.link'],
        fields: ['variables.elements.checkout.form.discountCode.link.styles.color'],
      },
      {
        type: 'element',
        id: 'discount-apply-button',
        isAvailable: ({ page }) => page?.type !== 'form',
        label: 'Apply button',
        resetKeys: ['variables.elements.checkout.form.discountCode.applyButton'],
        fields: ['variables.elements.checkout.form.discountCode.applyButton.styles.color'],
      },
      {
        type: 'element',
        id: 'discount-code-input',
        isAvailable: ({ page }) => page?.type !== 'form',
        label: 'Applied code',
        resetKeys: ['variables.elements.checkout.form.discountCode.code'],
        fields: [
          'variables.elements.checkout.form.discountCode.code.styles.color',
          'variables.elements.checkout.form.discountCode.code.styles.backgroundColor',
        ],
        fieldLabels: {
          'variables.elements.checkout.form.discountCode.code.styles.backgroundColor':
            'Background color',
        },
      },
      {
        type: 'element',
        id: 'discount-amount',
        isAvailable: ({ page }) => page?.type !== 'form',
        label: 'Discount amount',
        resetKeys: ['variables.elements.checkout.form.discountCode.discount'],
        fields: ['variables.elements.checkout.form.discountCode.discount.styles.color'],
      },
    ],
  },

  'variables.elements.checkout.form.manualPayments': {
    type: 'fieldgroup',
    id: 'manual-payments',
    divider: true,
    collapsible: true,
    isOnPage: ({ page }) => page?.paymentOptions?.some(option => option.type === 'manual'),
    fields: [
      {
        type: 'heading',
        label: 'Manual payments',
        description: 'Bank transfer and other manual payment methods.',
        level: 3,
        id: 'manual-payments',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.form.manualPayments',
          'variables.elements.checkout.form.manualPayments.name',
          'variables.elements.checkout.form.manualPayments.description',
          'variables.elements.checkout.form.manualPayments.instructions',
        ],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.form.manualPayments.styles.padding'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'manual-payments-method',
        label: 'Method',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.form.manualPayments.styles.backgroundColor',
          'variables.elements.checkout.form.manualPayments.selectedStyles.backgroundColor',
          'variables.elements.checkout.form.manualPayments.styles.borderColor',
          'variables.elements.checkout.form.manualPayments.selectedStyles.borderColor',
          'variables.elements.checkout.form.manualPayments.styles.borderWidth',
          'variables.elements.checkout.form.manualPayments.styles.borderRadius',
        ],
        fieldLabels: {
          'variables.elements.checkout.form.manualPayments.styles.borderColor': 'Border color',
          'variables.elements.checkout.form.manualPayments.styles.borderWidth': 'Border width',
          'variables.elements.checkout.form.manualPayments.styles.borderRadius': 'Border radius',
        },
      },
      {
        type: 'element',
        id: 'manual-payments-name',
        label: 'Name',
        toggleKey: 'variables.elements.checkout.form.manualPayments.name.enabled',
        fields: ['variables.elements.checkout.form.manualPayments.name.styles.color'],
        fontGroupBase: 'variables.elements.checkout.form.manualPayments.name.styles',
      },
      {
        type: 'element',
        id: 'manual-payments-description',
        label: 'Description',
        toggleKey: 'variables.elements.checkout.form.manualPayments.description.enabled',
        fields: ['variables.elements.checkout.form.manualPayments.description.styles.color'],
        fontGroupBase: 'variables.elements.checkout.form.manualPayments.description.styles',
      },
      {
        type: 'element',
        id: 'manual-payments-instructions',
        label: 'Instructions',
        toggleKey: 'variables.elements.checkout.form.manualPayments.instructions.enabled',
        fields: ['variables.elements.checkout.form.manualPayments.instructions.styles.color'],
        fontGroupBase: 'variables.elements.checkout.form.manualPayments.instructions.styles',
      },
    ],
  },

  // No section toggle and no eyes: while a page offers it, the box is how the
  // customer opts in, so a theme must not be able to hide it.
  'variables.elements.checkout.form.coverProcessingFees': {
    type: 'fieldgroup',
    id: 'cover-processing-fees',
    divider: true,
    collapsible: true,
    isOnPage: ({ page }) =>
      (page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT) &&
      getCoverProcessingFeesSetting(page)?.enabled === true,
    fields: [
      {
        type: 'heading',
        label: 'Processing fee',
        description: 'The checkbox customers tick to cover processing fees.',
        level: 3,
        id: 'cover-processing-fees',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.form.coverProcessingFees'],
      },
      { type: 'subheader', label: 'Style' },
      {
        type: 'columns',
        columns: 2,
        fields: ['variables.elements.checkout.form.coverProcessingFees.selector'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'cover-processing-fees-box',
        label: 'Card',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.form.coverProcessingFees.styles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.selectedStyles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.styles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.selectedStyles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.styles.borderWidth',
          'variables.elements.checkout.form.coverProcessingFees.styles.borderRadius',
        ],
        fieldLabels: {
          'variables.elements.checkout.form.coverProcessingFees.styles.borderColor': 'Border color',
          'variables.elements.checkout.form.coverProcessingFees.styles.borderWidth': 'Border width',
          'variables.elements.checkout.form.coverProcessingFees.styles.borderRadius':
            'Border radius',
        },
      },
      {
        type: 'element',
        id: 'cover-processing-fees-label',
        label: 'Label',
        showEye: false,
        fields: ['variables.elements.checkout.form.coverProcessingFees.label.styles.color'],
        fontGroupBase: 'variables.elements.checkout.form.coverProcessingFees.label.styles',
      },
      {
        type: 'element',
        id: 'cover-processing-fees-amount',
        label: 'Amount',
        showEye: false,
        fields: ['variables.elements.checkout.form.coverProcessingFees.amount.styles.color'],
        fontGroupBase: 'variables.elements.checkout.form.coverProcessingFees.amount.styles',
      },
      // No eye: the selector is what the customer taps.
      {
        type: 'element',
        id: 'cover-processing-fees-checkbox',
        label: 'Checkbox',
        showEye: false,
        fields: [
          'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.size',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderRadius',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.color',
        ],
      },
      {
        type: 'element',
        id: 'cover-processing-fees-switch',
        label: 'Switch',
        hideSwatch: true,
        showEye: false,
        fields: [
          'variables.elements.checkout.form.coverProcessingFees.switch.styles.size',
          'variables.elements.checkout.form.coverProcessingFees.switch.styles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.switch.selectedStyles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.switch.styles.color',
        ],
      },
      {
        type: 'element',
        id: 'cover-processing-fees-mark',
        label: 'Mark',
        showEye: false,
        fields: [
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.shape',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.size',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderRadius',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderWidth',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.backgroundColor',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.borderColor',
          'variables.elements.checkout.form.coverProcessingFees.indicator.styles.color',
        ],
      },
    ],
  },

  'variables.elements.checkout.form.priceBreakdown': {
    type: 'fieldgroup',
    id: 'price-breakdown',
    divider: true,
    collapsible: true,
    isOnPage: ({ page }) => page?.type === PAGE_TYPES.CHECKOUT || page?.type === PAGE_TYPES.EVENT,
    fields: [
      {
        type: 'heading',
        label: 'Price breakdown',
        description: 'Subtotal, taxes, fees, and total shown above the pay button.',
        level: 3,
        id: 'price-breakdown',
        resetable: true,
        toggleable: true,
        toggleKey: 'variables.elements.checkout.form.priceBreakdown.enabled',
        resetKeys: ['variables.elements.checkout.form.priceBreakdown'],
      },
      {
        type: 'conditional-wrapper',
        toggleKey: 'variables.elements.checkout.form.priceBreakdown.enabled',
        children: [
          { type: 'subheader', label: 'Layout' },
          {
            type: 'columns',
            columns: 1,
            fields: ['variables.elements.checkout.form.priceBreakdown.styles.alignment'],
          },
          { type: 'subheader', label: 'Fill' },
          'variables.elements.checkout.form.priceBreakdown.styles.color',
          { type: 'subheader', label: 'Typography' },
          {
            type: 'font-group',
            base: 'variables.elements.checkout.form.priceBreakdown.styles',
            columns: 2,
          },
          { type: 'subheader', label: 'Elements' },
          {
            type: 'element',
            id: 'breakdown-subtotal',
            label: 'Subtotal',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.subtotal.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.subtotal.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-order-bump',
            label: 'Order bump / Add-on',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.orderBump.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.orderBump.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-ticket',
            label: 'Ticket',
            isOnPage: ({ page }) => page?.type === PAGE_TYPES.EVENT,
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.ticket.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.ticket.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-ticket-fee',
            label: 'Booking fee',
            isOnPage: ({ page }) => page?.type === PAGE_TYPES.EVENT,
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.ticketFee.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.ticketFee.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-setup-fee',
            label: 'Setup fee',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.setupFee.enabled',

            fields: ['variables.elements.checkout.form.priceBreakdown.setupFee.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-bulk-discount',
            label: 'Bulk discount',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.bulkDiscount.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.bulkDiscount.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-coupon-discount',
            label: 'Coupon discount',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.couponDiscount.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.couponDiscount.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-tax',
            label: 'Tax',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.tax.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.tax.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-start-date',
            label: 'Start date',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.startDate.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.startDate.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-trial',
            label: 'Trial',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.trial.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.trial.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-fees',
            label: 'Fees',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.fees.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.fees.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-processing-fee-contribution',
            label: 'Processing fee contribution',
            // A customer who pays the contribution must always see its line.
            showEye: false,
            fields: [
              'variables.elements.checkout.form.priceBreakdown.processingFeeContribution.styles.color',
            ],
          },
          {
            type: 'element',
            id: 'breakdown-total',
            label: 'Total',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.total.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.total.styles.color'],
            fontGroupBase: 'variables.elements.checkout.form.priceBreakdown.total.styles',
          },
          {
            type: 'element',
            id: 'breakdown-due-now',
            label: 'Due now',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.dueNow.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.dueNow.styles.color'],
          },
          {
            type: 'element',
            id: 'breakdown-proration',
            label: 'Proration',
            toggleKey: 'variables.elements.checkout.form.priceBreakdown.proration.enabled',
            fields: ['variables.elements.checkout.form.priceBreakdown.proration.styles.color'],
          },
        ],
      },
    ],
  },

  'variables.elements.checkout.form.button': {
    type: 'fieldgroup',
    id: 'button',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Button',
        description: 'Primary pay or submit button.',
        level: 3,
        id: 'button',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.form.button',
          'variables.elements.checkout.form.button.priceOnButton',
        ],
      },
      { type: 'subheader', label: 'Fill' },
      {
        type: 'columns',
        columns: 2,
        fields: [
          'variables.elements.checkout.form.button.styles.backgroundColor',
          'variables.elements.checkout.form.button.hoverStyles.backgroundColor',
        ],
      },
      {
        type: 'columns',
        columns: 2,
        fields: [
          { path: 'variables.elements.checkout.form.button.styles.color', label: 'Text color' },
          'variables.elements.checkout.form.button.hoverStyles.color',
        ],
      },
      { type: 'subheader', label: 'Stroke' },
      {
        type: 'columns',
        columns: 2,
        fields: [
          {
            path: 'variables.elements.checkout.form.button.styles.borderColor',
            label: 'Border color',
          },
          'variables.elements.checkout.form.button.hoverStyles.borderColor',
        ],
      },
      {
        type: 'columns',
        columns: 2,
        fields: [
          {
            path: 'variables.elements.checkout.form.button.styles.borderWidth',
            label: 'Border width',
          },
          {
            path: 'variables.elements.checkout.form.button.styles.borderRadius',
            label: 'Border radius',
          },
        ],
      },
      { type: 'subheader', label: 'Typography' },
      {
        type: 'font-group',
        base: 'variables.elements.checkout.form.button.styles',
        columns: 2,
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.form.button.styles.padding'],
      },
      {
        type: 'label-toggle',
        size: 'sm',
        label: 'Price on button',
        id: 'variables.elements.checkout.form.button.priceOnButton',
        toggleKey: 'variables.elements.checkout.form.button.priceOnButton.enabled',
      },
    ],
  },

  'variables.elements.checkout.form.trustBadges': {
    type: 'fieldgroup',
    id: 'trust-badges',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Trust badges',
        description: 'Trust signals shown near the pay button.',
        level: 3,
        id: 'trust-badges',
        resetable: true,
        toggleable: false,
        resetKeys: [
          'variables.elements.checkout.form.orPayWithCard',
          'variables.elements.checkout.form.securePayments',
          'variables.elements.poweredByStripe',
          'variables.elements.poweredByCheckoutPage',
        ],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'trust-or-pay-with-card',
        label: 'Or pay with card',
        hideSwatch: true,
        toggleKey: 'variables.elements.checkout.form.orPayWithCard.enabled',
        resetKeys: ['variables.elements.checkout.form.orPayWithCard'],
        fields: [
          'variables.elements.checkout.form.orPayWithCard.styles.color',
          'variables.elements.checkout.form.orPayWithCard.styles.borderColor',
        ],
        fieldLabels: {
          'variables.elements.checkout.form.orPayWithCard.styles.color': 'Text color',
          'variables.elements.checkout.form.orPayWithCard.styles.borderColor': 'Border color',
        },
      },
      {
        type: 'element',
        id: 'trust-secure-payments',
        label: 'Secure payments',
        hideSwatch: true,
        toggleKey: 'variables.elements.checkout.form.securePayments.enabled',
        resetKeys: ['variables.elements.checkout.form.securePayments'],
        fields: ['variables.elements.checkout.form.securePayments.styles.color'],
        fieldLabels: {
          'variables.elements.checkout.form.securePayments.styles.color': 'Text color',
        },
        fontGroupBase: 'variables.elements.checkout.form.securePayments.styles',
      },
      {
        type: 'element',
        id: 'trust-powered-by-stripe',
        label: 'Powered by Stripe',
        toggleKey: 'variables.elements.poweredByStripe.enabled',
        resetKeys: ['variables.elements.poweredByStripe'],
        fields: ['variables.elements.poweredByStripe.styles.badgeStyle'],
        // Mutually exclusive with our branding badge, and only paying sellers can
        // enable it (free/trial show our badge instead). See themeFieldBuilder.
        brandingBadge: 'stripe',
        mutuallyExclusiveWith: 'variables.elements.poweredByCheckoutPage.enabled',
        lockMessage: 'Upgrade to show the Stripe trust badge on your checkout.',
      },
      {
        type: 'element',
        // id must not end in "-page" (that maps to the document icon); keep it in
        // line with trust-powered-by-stripe so it gets the same badge icon tile.
        id: 'trust-powered-by-checkoutpage',
        label: 'Powered by Checkout Page',
        toggleKey: 'variables.elements.poweredByCheckoutPage.enabled',
        resetKeys: ['variables.elements.poweredByCheckoutPage'],
        // No style fields — it's a pure on/off badge row.
        fields: [],
        // Our branding badge. Forced on for free/trial (locked); only paying sellers
        // can turn it off. Mutually exclusive with the Stripe badge above.
        brandingBadge: 'checkoutpage',
        mutuallyExclusiveWith: 'variables.elements.poweredByStripe.enabled',
        lockMessage: 'Upgrade to remove Checkout Page branding.',
      },
    ],
  },

  'variables.elements.checkout.confirmation': {
    type: 'fieldgroup',
    id: 'confirmation',
    divider: true,
    collapsible: true,

    fields: [
      {
        type: 'heading',
        label: 'Confirmation',
        description: 'Success screen shown after payment.',
        level: 3,
        id: 'confirmation',
        resetable: true,
        toggleable: false,
        resetKeys: ['variables.elements.checkout.confirmation'],
      },
      { type: 'subheader', label: 'Layout' },
      {
        type: 'columns',
        columns: 1,
        fields: ['variables.elements.checkout.confirmation.mark.styles.size'],
      },
      { type: 'subheader', label: 'Elements' },
      {
        type: 'element',
        id: 'confirmation-mark',
        label: 'Mark',
        fields: ['variables.elements.checkout.confirmation.mark.styles.color'],
      },
      {
        type: 'element',
        id: 'confirmation-heading',
        label: 'Heading',
        fields: ['variables.elements.checkout.confirmation.heading.styles.color'],
      },
      {
        type: 'element',
        id: 'confirmation-text',
        label: 'Text',
        fields: ['variables.elements.checkout.confirmation.text.styles.color'],
      },
    ],
  },
};
